import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  careerEntryService,
  type CreateCareerEntryRequest,
  type UpdateCareerEntryRequest,
} from "@/api/services/career-entry.service";

export const careerEntryKeys = {
  all: ["career-entries"] as const,
  list: (playerId: number) => [...careerEntryKeys.all, playerId] as const,
};

export function useCareerEntries(playerId: number | null) {
  return useQuery({
    queryKey: careerEntryKeys.list(playerId ?? 0),
    queryFn: () => careerEntryService.list(playerId as number),
    enabled: !!playerId,
  });
}

export function useCreateCareerEntry(playerId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateCareerEntryRequest) =>
      careerEntryService.create(playerId, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: careerEntryKeys.list(playerId) });
    },
  });
}

export function useUpdateCareerEntry(playerId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      entryId,
      data,
    }: {
      entryId: number;
      data: UpdateCareerEntryRequest;
    }) => careerEntryService.update(playerId, entryId, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: careerEntryKeys.list(playerId) });
    },
  });
}

export function useDeleteCareerEntry(playerId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (entryId: number) =>
      careerEntryService.remove(playerId, entryId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: careerEntryKeys.list(playerId) });
    },
  });
}
